import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("abhivesha_cart")) || [];
    } catch {
      return [];
    }
  });
  const [coupon, setCoupon] = useState(() => localStorage.getItem("abhivesha_coupon") || "");
  const [coupons, setCoupons] = useState({});
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("abhivesha_cart", JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem("abhivesha_coupon", coupon);
  }, [coupon]);

  useEffect(() => {
    api.get("/coupons").then(({ data }) => setCoupons(Object.fromEntries(data.map((c) => [c.code, c])))).catch(() => {});
  }, []);

  const add = (product, qty = 1, silent = false) => {
    setItems((prev) => {
      const found = prev.find((i) => i.slug === product.slug);
      if (found) {
        return prev.map((i) => (i.slug === product.slug ? { ...i, qty: Math.min(20, i.qty + qty) } : i));
      }
      return [...prev, { slug: product.slug, name: product.name, mascot: product.mascot, price: product.price, image: product.image, accent: product.accent, qty }];
    });
    if (!silent) toast.success(`${product.name} added to cart`);
  };

  const setQty = (slug, qty) => {
    if (qty <= 0) return remove(slug);
    setItems((prev) => prev.map((i) => (i.slug === slug ? { ...i, qty: Math.min(20, qty) } : i)));
  };

  const remove = (slug) => setItems((prev) => prev.filter((i) => i.slug !== slug));
  const clear = () => setItems([]);

  const applyCoupon = (code) => {
    const c = (code || "").trim().toUpperCase();
    if (!c) return "Enter a code";
    if (!coupons[c]) return "Invalid or expired code";
    setCoupon(c);
    toast.success(`${c} applied — ${coupons[c].label}`);
    return null;
  };

  const removeCoupon = () => setCoupon("");

  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);
  const subtotal = useMemo(() => items.reduce((s, i) => s + i.qty * i.price, 0), [items]);
  const distinct = useMemo(() => new Set(items.map((i) => i.slug)).size, [items]);
  const bundleRate = distinct >= 5 ? 15 : distinct >= 3 ? 10 : 0;
  const bundleDiscount = Math.round((subtotal * bundleRate) / 100);
  const couponDef = coupons[coupon];
  const couponDiscount = couponDef && couponDef.kind === "percent" ? Math.round((subtotal * couponDef.value) / 100) : 0;
  const shippingWaived = !!couponDef && couponDef.kind === "freeship";
  const discount = bundleDiscount + couponDiscount;

  return (
    <CartContext.Provider
      value={{ items, add, remove, setQty, clear, count, subtotal, distinct, bundleRate, bundleDiscount, discount, coupon, couponDef, couponDiscount, shippingWaived, applyCoupon, removeCoupon, isOpen, openCart: () => setIsOpen(true), closeCart: () => setIsOpen(false) }}
    >
      {children}
    </CartContext.Provider>
  );
}
